import { lazy, Suspense, useEffect, useRef, useState } from "react";

const HomeBelowFold = lazy(async () => {
  const module = await import("@/components/site/home-below-fold");
  return { default: module.HomeBelowFold };
});

const belowFoldHashes = new Set([
  "#stay",
  "#amenities",
  "#explore",
  "#gallery",
  "#rates",
  "#reviews",
  "#fit",
  "#faq",
]);

function scrollToHash() {
  const id = window.location.hash.slice(1);
  if (!id) return;
  document.getElementById(id)?.scrollIntoView();
}

export function DeferredHomeBelowFold() {
  const ref = useRef<HTMLDivElement>(null);
  const [load, setLoad] = useState(false);

  useEffect(() => {
    const loadFromHash = () => {
      if (belowFoldHashes.has(window.location.hash)) {
        setLoad(true);
      }
    };
    loadFromHash();
    window.addEventListener("hashchange", loadFromHash);
    return () => window.removeEventListener("hashchange", loadFromHash);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el || load) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: "400px 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [load]);

  useEffect(() => {
    if (!load || !belowFoldHashes.has(window.location.hash)) return;

    let cancelled = false;
    let attempt = 0;
    const tryScroll = () => {
      if (cancelled) return;
      if (document.getElementById(window.location.hash.slice(1))) {
        scrollToHash();
        return;
      }
      if (attempt < 30) {
        attempt += 1;
        requestAnimationFrame(tryScroll);
      }
    };
    tryScroll();
    return () => {
      cancelled = true;
    };
  }, [load]);

  if (!load) {
    return <div ref={ref} id="stay" className="min-h-[240vh]" aria-hidden />;
  }

  return (
    <Suspense fallback={<div id="stay" className="min-h-[240vh]" aria-hidden />}>
      <HomeBelowFold />
    </Suspense>
  );
}
